import { io } from "socket.io-client";
import { Socket } from "socket.io-client/build/esm/socket";
import { AuthService } from "@service/auth.service";
import { AudioMessageStream } from "@models/audio-message-stream";
import { EventType } from "@models/audio-message";
import { debugCompanionPacing } from "@utils/companion-pacing-debug";

const USER_PCM_LOG_INTERVAL_MS = 1000;
/** If no PCM for this long, treat the next chunk as a new utterance and flush partial counters. */
const USER_PCM_BURST_GAP_MS = 2000;

export enum StreamState {
  Transcription = "Transcription",
  Processing = "Processing",
  Streaming = "Streaming",
  Completed = "Completed",
}

const BASE_API_URL = process.env.REACT_APP_API_URL as string;

export type CompanionResponsePayload =
  | string
  | { text: string; streamSeq?: number };

export class CompanionStreamingService {
  private readonly socket: Socket;
  private readonly authService = AuthService.instance;
  private readonly connected: Promise<void>;

  /** Throttle debug spam: one PCM chunk ≈ one WS message (many per second while speaking). */
  private userPcmLogWindowStartMs = 0;
  private userPcmChunksInWindow = 0;
  private userPcmBytesInWindow = 0;
  private userPcmLastSessionId: string | undefined;
  private userPcmLastChunkAtMs = 0;

  private readonly onResponseHandler = (payload: CompanionResponsePayload) => {
    if (typeof payload === "string") {
      this.onResponse?.(payload, undefined);
      return;
    }
    this.onResponse?.(payload.text, payload.streamSeq);
  };

  private readonly onStateChangeHandler = (state: StreamState) => {
    this.onStateChange?.(state);
  };

  constructor(
    private readonly onResponse: (chunk: string, streamSeq?: number) => void,
    private readonly onStateChange: (state: StreamState) => void,
  ) {
    this.socket = io(`${BASE_API_URL}/companion`, {
      transports: ["websocket"],
      auth: async (cb) => {
        const token = await this.authService.getAccessToken();
        cb({ token });
      },
    });

    this.connected = new Promise<void>((resolve) => {
      this.socket.on("connect", resolve);
    });

    this.socket.on("response", this.onResponseHandler);

    this.socket.on("stateChange", this.onStateChangeHandler);
  }

  private flushUserPcmPacingLog(reason: string): void {
    if (this.userPcmChunksInWindow === 0) {
      return;
    }
    debugCompanionPacing(`WebSocket UserInput PCM summary (${reason})`, {
      sessionId: this.userPcmLastSessionId,
      chunks: this.userPcmChunksInWindow,
      bytes: this.userPcmBytesInWindow,
    });
    this.userPcmLogWindowStartMs = 0;
    this.userPcmChunksInWindow = 0;
    this.userPcmBytesInWindow = 0;
    this.userPcmLastChunkAtMs = 0;
  }

  private logWebSocketEmit(
    data: Omit<AudioMessageStream, "audio">,
    audioBytes: number,
  ): void {
    if (process.env.REACT_APP_DEBUG_COMPANION_PACING !== "true") {
      return;
    }

    const pacing = data.conversationPacing ?? "balanced";
    const isUserPcm =
      data.eventType === EventType.UserInput && audioBytes > 0;

    if (!isUserPcm) {
      this.flushUserPcmPacingLog("non-UserInput or empty audio");
      debugCompanionPacing("WebSocket emit companion/message", {
        sessionId: data.sessionId,
        eventType: data.eventType,
        streamSeq: data.streamSeq,
        conversationPacing: pacing,
        audioBytes,
      });
      return;
    }

    const now = Date.now();
    const gapSinceLastChunk =
      this.userPcmLastChunkAtMs > 0
        ? now - this.userPcmLastChunkAtMs
        : USER_PCM_BURST_GAP_MS;

    if (
      data.sessionId !== this.userPcmLastSessionId ||
      gapSinceLastChunk >= USER_PCM_BURST_GAP_MS
    ) {
      this.flushUserPcmPacingLog("session or burst boundary");
      this.userPcmLastSessionId = data.sessionId;
      this.userPcmLogWindowStartMs = now;
    }

    this.userPcmLastChunkAtMs = now;
    this.userPcmChunksInWindow += 1;
    this.userPcmBytesInWindow += audioBytes;

    if (now - this.userPcmLogWindowStartMs >= USER_PCM_LOG_INTERVAL_MS) {
      debugCompanionPacing(
        "WebSocket UserInput PCM (rolling 1s; many small chunks expected)",
        {
          sessionId: data.sessionId,
          conversationPacing: pacing,
          chunksThisSecond: this.userPcmChunksInWindow,
          bytesThisSecond: this.userPcmBytesInWindow,
        },
      );
      this.userPcmLogWindowStartMs = now;
      this.userPcmChunksInWindow = 0;
      this.userPcmBytesInWindow = 0;
    }
  }

  async sendMessage(message: AudioMessageStream): Promise<void> {
    await this.connected;

    const { audio, ...data } = message;

    const audioBytes =
      audio == null
        ? 0
        : audio instanceof ArrayBuffer
          ? audio.byteLength
          : "byteLength" in audio
            ? audio.byteLength
            : 0;

    this.logWebSocketEmit(data, audioBytes);

    this.socket.emit("message", data, audio);
  }

  destroy(): void {
    this.flushUserPcmPacingLog("destroy");
    this.socket.off("response", this.onResponseHandler);
    this.socket.off("stateChange", this.onStateChangeHandler);
    this.socket.disconnect();
  }
}
